import { useState } from "react";
import moment from "moment";

import { Button, Label, ModalDelete } from "expano-components";

import { Main, PageLoader, Error, CampaignStatsChart } from "components";
import useGet from "hooks/useGet";
import useCreate from "hooks/useCreate";
import useDelete from "hooks/useDelete";

import { ReactComponent as ProductIcon } from "icons/product.svg";

import BasicData from "./components/BasicData";
import CampaignProducts from "./components/CampaignProducts";
import CampaignFilters from "./components/CampaignFilters";
import Cpc from "./components/Cpc";

const ChannelCampaignEdit = ({
  match: {
    params: { channel_id, channel_campaign_id, channel_campaign_group_id },
  },
  history,
}) => {
  const [show_delete_modal, setShowDeleteModal] = useState(false);

  const {
    is_loading,
    is_error,
    error,
    data: channel_campaign_group,
    refetch,
  } = useGet({
    query: ["channel_campaigns", channel_campaign_group_id],
    path: `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}`,
    context: "ui_show",
  });

  const { is_deleting, _delete } = useDelete({
    path: `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}`,
    onSuccess: () => history.push(`/campaigns/channels/${channel_id}`),
  });

  const { is_creating: is_exporting, create: exportToAllegro } = useCreate({
    path: `/channels/${channel_id}/channel_campaigns/${channel_campaign_id}/channel_campaign_groups/${channel_campaign_group_id}/export`,
    onSuccess: refetch,
    success_message: "Wyexportowano pomyślnie",
  });

  if (is_loading) {
    return <PageLoader />;
  }

  if (is_error) {
    return <Error message={error} />;
  }

  return (
    <Main
      page={{
        name: (
          <>
            {channel_campaign_group?.name}{" "}
            {channel_campaign_group?.sync_needed ? (
              <Label
                type="fill"
                text="Wymaga aktualizacji na Allegro"
                color="#ff0000"
              />
            ) : (
              <Label
                type="fill"
                text={`Ostatnia synchronizacja z Allegro: ${
                  channel_campaign_group?.last_synced_at
                    ? moment(channel_campaign_group.last_synced_at).format(
                        "DD.MM.YYYY HH:mm",
                      )
                    : "-"
                }`}
                color="#2f7033"
              />
            )}
          </>
        ),
        icon: <ProductIcon />,
        breadcrumbs: [
          {
            name: "Kampanie reklamowe",
            path: `/campaigns/channels/${channel_id}`,
          },
          {
            name: channel_campaign_group?.channel_campaign?.name,
            path: `/campaigns/channels/${channel_id}/channel_campaigns/${channel_campaign_id}`,
          },
          { name: channel_campaign_group?.name },
        ],
        buttons: (
          <>
            <Button
              type="delete"
              text="Usuń grupę kampanii"
              disabled={is_deleting}
              onClick={() => setShowDeleteModal(true)}
            />
            {channel_campaign_group?.sync_needed && (
              <Button
                text="Synchronizuj z Allegro"
                disabled={is_exporting}
                onClick={() => exportToAllegro({})}
              />
            )}
          </>
        ),
      }}
    >
      {show_delete_modal && (
        <ModalDelete
          handleDelete={_delete}
          handleClose={() => setShowDeleteModal(false)}
          name={`grupę kampanii ${channel_campaign_group?.name}?`}
        />
      )}
      <CampaignStatsChart
        channel_id={channel_id}
        channel_campaign_id={channel_campaign_id}
        channel_campaign_group_id={channel_campaign_group_id}
      />
      <div className="dashboard-wrapper-flex">
        <div className="column-half">
          <BasicData
            channel_campaign_group={channel_campaign_group}
            channel_campaign_id={channel_campaign_id}
            channel_id={channel_id}
            refetch={refetch}
            initialValues={{
              offers_export_type: channel_campaign_group?.offers_export_type,
              name: channel_campaign_group?.name,
              kind: channel_campaign_group?.kind,
              status: channel_campaign_group?.status,
              sales_period_standard_cpc:
                channel_campaign_group?.sales_period_standard_cpc,
              budget_daily_amount: channel_campaign_group?.budget_daily_amount,
              budget_total_amount: channel_campaign_group?.budget_total_amount,
              display_start_date: channel_campaign_group?.display_start_date
                ? moment(channel_campaign_group.display_start_date).toDate()
                : null,
              display_end_date: channel_campaign_group?.display_end_date
                ? moment(channel_campaign_group.display_end_date).toDate()
                : null,
              placements: channel_campaign_group?.placements,
            }}
          />
        </div>
        <div className="column-half">
          {channel_campaign_group?.offers_export_type === "offer_ids" ? (
            <CampaignProducts
              channel_campaign_group={channel_campaign_group}
              channel_campaign_id={channel_campaign_id}
              channel_id={channel_id}
              refetch={refetch}
            />
          ) : (
            <CampaignFilters channel_campaign_group={channel_campaign_group} />
          )}
          <Cpc
            channel_id={channel_id}
            channel_campaign_id={channel_campaign_id}
            channel_campaign_group={channel_campaign_group}
            refetch={refetch}
            initialValues={{
              sales_period_min_cpc_percent:
                channel_campaign_group?.sales_period_min_cpc_percent,
              sales_period_max_cpc_percent:
                channel_campaign_group?.sales_period_max_cpc_percent,
              sales_period_standard_cpc:
                channel_campaign_group?.sales_period_standard_cpc,
            }}
          />
        </div>
      </div>
    </Main>
  );
};

export default ChannelCampaignEdit;
